/**
 * @file        src/layout/Sidebar.jsx
 * @description Admin sidebar. Exclusive accordion groups; auto-opens the
 *              group containing the current route.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-10-09 Claude — GROUP_MODULE maps the new 'SEO Management' group to the
 *                          existing 'Site Management' module — the backend gates
 *                          /api/seo on that same key (routes/seo.routes.js).
 *   - 2026-09-25 Claude — The open accordion's height is DERIVED from each group's
 *                          visible-child count (`--subnav-rows`) instead of the flat
 *                          480px cap in admin.css, which clipped any group that
 *                          outgrew it with no scrollbar and no hint. Account
 *                          Management (15 rows, 510px) lost its last entry —
 *                          Communication Log — the moment Action Centre and
 *                          Activity Log were added above it. The collapsed rail's
 *                          flyout gained a max-height + scroll for the same reason.
 *   - 2026-09-23 Claude — A document page (invoice / e-ticket / voucher) opened
 *                          from a Corporate screen now keeps the Corporate
 *                          group open. Those routes are shared by both
 *                          audiences, so the longest-prefix rule below always
 *                          answered "B2C Flight"/"B2C Hotel"; the screen that
 *                          linked there says which it was (lib/docOrigin).
 *   - 2026-09-15 Claude — Corporate Flight/Hotel map to their OWN module keys
 *                          instead of the B2C ones, and Corporate Dashboard (a
 *                          LEAF) is gated too. Leaves were blanket-visible to
 *                          every staff account, which was harmless while
 *                          Dashboard was the only leaf and wrong the moment
 *                          Corporate Dashboard joined it.
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-07-09 Claude — Collapsed (icon-rail) mode: hovering a group now shows
 *                          a flyout with its inner modules (children), clickable.
 *                          The flyout is fixed-positioned via a portal so it
 *                          escapes the nav-scroll's overflow clipping.
 *   - 2026-07-27 Claude — First real (non-cosmetic) role gate: the "Platform"
 *                          section (Tenant Management, Platform Admin Users)
 *                          only renders for role === 'super_admin'. The actual
 *                          security boundary is the backend's
 *                          authorize('super_admin') — this is UX only.
 *   - 2026-07-31 Claude — Brand logos come from the tenant's Site Settings
 *                          (resolved from the domain); the bundled PNGs are now
 *                          only the fallback. Nav/roles untouched.
 *   - 2026-08-03 Claude — The role gate now cuts both ways: a super_admin sees
 *                          ONLY the Platform section (every tenant-operations
 *                          API 403s for them anyway); a tenant_admin never sees
 *                          Platform. Backend authorize() remains the boundary.
 *   - 2026-08-04 Claude — Groups can now be marked `disabled` in nav.js; Group
 *                          renders them greyed-out and inert (no expand, no
 *                          flyout, no NavLink) instead of a normal accordion.
 *                          First use: Marketing Management.
 *   - 2026-08-04 Claude — Staff accounts (Staff Management) now see only the
 *                          groups their granted "Software Access" modules
 *                          cover (GROUP_MODULE below) — same UX-only caveat as
 *                          the Platform-section gate above it: the real
 *                          boundary is middleware/requireModule.js on the
 *                          backend, this just avoids showing a link that would
 *                          403. 'Staff Management' itself is never shown to
 *                          staff, full stop — see routes/staff.routes.js for
 *                          why granting it would be a privilege escalation.
 */
import { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { NAV } from '../lib/nav';
import { isCorporateOrigin } from '../lib/docOrigin';
import Icon from '../components/Icon';
import logoFull from '../assets/logo-full.png';
import logoMark from '../assets/logo-mark.png';
import { useAuth } from '../lib/auth.store';
import { useBranding } from '../lib/useBranding';
import { toast } from '../lib/toast';

// A nav child marked `hidden` stays routable (and stays in nav.js, so it is easy
// to restore) but is never rendered — used for pages reachable from a button on
// their own list page, e.g. "Add Markup" / "Add Discount".
const visibleChildren = (item) => (item.children || []).filter((c) => !c.hidden);

// Approx. rendered height of one .rail-flyout-item, for the flyout's placement
// estimate only (never for clipping — the flyout scrolls).
const FLYOUT_ROW_PX = 38;

// Sidebar GROUP label -> the backend "Software Access" module key(s) that gate
// it (middleware/requireModule.js), for a 'staff' role only. A group with no
// entry here (Marketing Management — already `disabled`) is left alone.
// 'Staff Management' has no entry — see the changelog: staff can never see or
// reach it, module grant or not.
//
// Corporate Flight/Hotel DO share the backend routes with their B2C
// counterparts (one `audience` param tells them apart), but they are no longer
// gated by the same key: middleware/audienceModule narrows those shared routes
// per request, so the two halves are genuinely separate grants. Mapping them
// to 'B2C … Management' — what this file did until 2026-09-15 — is what put
// Corporate Flight in the sidebar of a staff member who was never given it.
const GROUP_MODULE = {
  'Account Management': 'Account Management',
  'Wallet Management': 'Wallet Management',
  'Airport Management': 'Airport Management',
  'API Management': 'API Management',
  'B2C Flight': 'B2C Flight Management',
  'B2C Hotel': 'B2C Hotel Management',
  'Corporate Flight': 'Corporate Flight Management',
  'Corporate Hotel': 'Corporate Hotel Management',
  'B2C Insurance': 'B2C Insurance Management',
  'Corporate Insurance': 'Corporate Insurance Management',
  'Customer Management': 'Customer Management',
  'Blog Management': 'Blog Management',
  'Package Management': 'Package Management',
  'Payment Management': 'Payment Management',
  'Query Management': 'Query Management',
  'Testimonial Management': 'Testimonial Management',
  'FAQ Management': 'FAQ Management',
  'Offer Management': 'Offer Management',
  'Site Management': 'Site Management',
  'SEO Management': 'Site Management',
};

// Sidebar LEAF label -> module key. Leaves used to be blanket-visible
// (`if (it.kind === 'leaf') return true`), which was fine while Dashboard was
// the only one — but Corporate Dashboard is a leaf too, so every staff account
// could see corporate revenue, volume and credit regardless of grant. The plain
// Dashboard stays open to all staff (it is the landing page, and it is the
// retail view); anything not listed here is open, same as before.
const LEAF_MODULE = {
  'Corporate Dashboard': 'Corporate Flight Management',
};

// True when this nav item should render for the current user. tenant_admin
// and super_admin are unrestricted (module concept doesn't apply to them);
// 'staff' needs the mapped module in their grant, or — for a group with no
// mapping at all ('Staff Management') — is always excluded.
function itemVisible(it, role, modules) {
  if (role !== 'staff') return true;
  if (it.kind === 'leaf') {
    const leafKey = LEAF_MODULE[it.label];
    return leafKey ? (modules || []).includes(leafKey) : true;
  }
  const required = GROUP_MODULE[it.label];
  if (!required) return false;
  const keys = Array.isArray(required) ? required : [required];
  return keys.some((k) => (modules || []).includes(k));
}

// A B2C group and the Corporate group that shares its routes. The document
// pages (/flight/bookings/:id/ticket, /hotel/bookings/:id/invoice, …) are ONE
// route per product used by BOTH screens, so the path can only ever resolve to
// the B2C child below — which is why opening a corporate booking's invoice
// opened the B2C Flight menu. The list that linked there records where it came
// from (lib/docOrigin); this maps that back onto the group. Presentation only:
// the document is fetched by booking id, so nothing about WHAT is shown moves
// with it (§9d).
const CORPORATE_TWIN = {
  'B2C Flight': 'Corporate Flight',
  'B2C Hotel': 'Corporate Hotel',
  'B2C Insurance': 'Corporate Insurance',
};

// Which sidebar group owns this URL? An exact child match wins; otherwise fall
// back to the LONGEST child path the URL sits under, so a page with no visible
// entry of its own — /hotel/markups/new, /hotel/markups/7/edit — still keeps its
// group ("B2C Hotel") open. HIDDEN children count here: they own a URL even
// though they don't render.
function findOwningGroup(pathname, search) {
  let best = null;
  let bestLen = -1;
  for (const sec of NAV) for (const it of sec.items) {
    if (it.kind !== 'group') continue;
    for (const c of it.children || []) {
      if (!c.path) continue;
      // An exact match is the screen itself and owns itself outright — only a
      // deeper page (a shared document route) can have come from elsewhere.
      if (c.path === pathname) return it.label;
      if (pathname.startsWith(`${c.path}/`) && c.path.length > bestLen) {
        best = it.label;
        bestLen = c.path.length;
      }
    }
  }
  if (best && isCorporateOrigin(search)) return CORPORATE_TWIN[best] || best;
  return best;
}

export default function Sidebar({ open, collapsed, onClose }) {
  const { pathname, search } = useLocation();
  const initialGroup = useMemo(() => findOwningGroup(pathname, search), [pathname, search]);
  const [openGroup, setOpenGroup] = useState(initialGroup);
  const logout = useAuth((s) => s.logout);
  const role = useAuth((s) => s.user?.role);
  const modules = useAuth((s) => s.user?.modules);
  const { logoUrl, markUrl, name } = useBranding();

  useEffect(() => { const g = findOwningGroup(pathname, search); if (g) setOpenGroup(g); }, [pathname, search]);

  async function handleLogout() {
    await logout();
    toast.ok('Logged out', 'You have been signed out securely.');
  }

  return (
    <aside className={`sidebar glass ${open ? 'open' : ''}`}>
      {/* Tenant logos when configured, bundled defaults otherwise — and also if
          a configured URL fails to load. The mini rail uses the square favicon,
          the only square brand asset Site Settings stores. */}
      <div className="brand">
        <img
          src={logoUrl || logoFull}
          alt={name || 'Neon Holidays'}
          className="brand-logo"
          onError={(e) => { e.currentTarget.src = logoFull; }}
        />
        <img
          src={markUrl || logoMark}
          alt={name ? name.charAt(0) : 'N'}
          className="brand-logo-mini"
          onError={(e) => { e.currentTarget.src = logoMark; }}
        />
      </div>
      <span className="brand-sub">Travel-Tech Admin Console</span>

      <div className="nav-scroll">
        {NAV.filter((sec) => (role === 'super_admin' ? sec.section === 'Platform' : sec.section !== 'Platform'))
          .map((sec) => ({ ...sec, items: sec.items.filter((it) => itemVisible(it, role, modules)) }))
          // A staff account with no module in this section would otherwise show
          // a bare "Operations"/"Content"/"System" label over an empty group.
          .filter((sec) => sec.items.length > 0)
          .map((sec) => (
          <div key={sec.section}>
            <p className="nav-label">{sec.section}</p>
            {sec.items.map((it) => it.kind === 'leaf' ? (
              <NavLink
                key={it.path}
                to={it.path}
                end
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                onClick={onClose}
              >
                <Icon name={it.icon} />
                <span>{it.label}</span>
              </NavLink>
            ) : (
              <Group
                key={it.label}
                item={it}
                collapsed={collapsed}
                isOpen={openGroup === it.label}
                onToggle={() => setOpenGroup(openGroup === it.label ? null : it.label)}
                onLeafClick={onClose}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="side-foot">
        <button className="logout-btn" onClick={handleLogout}>
          <Icon name="logout" /> Log Out
        </button>
      </div>
    </aside>
  );
}

function Group({ item, collapsed, isOpen, onToggle, onLeafClick }) {
  // Collapsed rail: hovering the icon shows a fixed-positioned flyout with the
  // group's children (the nav-scroll clips overflow, so the flyout is portaled
  // to <body> and positioned from the icon's rect). Expanded: normal accordion.
  const [flyout, setFlyout] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const rowRef = useRef(null);
  const closeTmr = useRef(null);

  // `item.disabled` groups (e.g. Marketing Management) render greyed-out and
  // inert — no expand, no flyout, no navigation — until re-enabled in nav.js.
  if (item.disabled) {
    return (
      <div className="nav-group nav-group-disabled">
        <a className="nav-item disabled" aria-disabled="true" title="Coming in a future release">
          <Icon name={item.icon} />
          <span>{item.label}</span>
        </a>
      </div>
    );
  }

  const openFlyout = () => {
    if (!collapsed) return;
    clearTimeout(closeTmr.current);
    const r = rowRef.current?.getBoundingClientRect();
    if (r) {
      // Clamp so a tall flyout never runs off the bottom: est. height = title +
      // rows, then keep an 8px viewport margin. This is a best-effort POSITION
      // only — .rail-flyout also carries its own max-height + scroll, so a group
      // taller than the viewport stays fully reachable rather than trusting this
      // estimate to be right.
      const est = 48 + visibleChildren(item).length * FLYOUT_ROW_PX;
      const top = Math.max(8, Math.min(r.top, window.innerHeight - est - 8));
      setPos({ top, left: r.right + 8 });
    }
    setFlyout(true);
  };
  // Small close delay so moving the cursor across the gap to the flyout doesn't
  // dismiss it.
  const scheduleClose = () => {
    clearTimeout(closeTmr.current);
    closeTmr.current = setTimeout(() => setFlyout(false), 120);
  };
  useEffect(() => () => clearTimeout(closeTmr.current), []);

  return (
    <div className="nav-group" onMouseEnter={openFlyout} onMouseLeave={scheduleClose}>
      <a
        ref={rowRef}
        className={`nav-item ${isOpen ? 'open' : ''}`}
        // In collapsed mode a click still toggles the (hidden) accordion state,
        // harmless; the flyout is the real interaction there.
        onClick={onToggle}
      >
        <Icon name={item.icon} />
        <span>{item.label}</span>
        <Icon name="chev" className="chev" />
      </a>

      {/* Inline accordion — shown in EXPANDED mode (CSS hides it when collapsed).
          `--subnav-rows` is what the open max-height is computed FROM, so a group
          can never outgrow its own cap and silently lose its last entries (which
          is what hid Communication Log — see admin.css .subnav.show). It counts
          the VISIBLE children, because a `hidden: true` child renders nothing and
          must not buy height. */}
      <div
        className={`subnav ${isOpen ? 'show' : ''}`}
        style={{ '--subnav-rows': visibleChildren(item).length }}
      >
        {visibleChildren(item).map((c) => (
          <NavLink
            key={c.path}
            to={c.path}
            end
            className={({ isActive }) => `sub ${isActive ? 'active' : ''}`}
            onClick={onLeafClick}
          >
            {c.label}
          </NavLink>
        ))}
      </div>

      {/* Collapsed-rail hover flyout — portaled so it isn't clipped by the rail. */}
      {collapsed && flyout && createPortal(
        <div
          className="rail-flyout"
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
          onMouseEnter={openFlyout}
          onMouseLeave={scheduleClose}
        >
          <p className="rail-flyout-title">{item.label}</p>
          {visibleChildren(item).map((c) => (
            <NavLink
              key={c.path}
              to={c.path}
              end
              className={({ isActive }) => `rail-flyout-item ${isActive ? 'active' : ''}`}
              onClick={() => { setFlyout(false); onLeafClick?.(); }}
            >
              {c.label}
            </NavLink>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
